import { useCallback, useEffect, useRef, useState } from "react";
import { isMobileViewport } from "./mobile";
import { useAuth } from "../../auth/AuthProvider";
import { useI18n } from "../../i18n";
import { shouldAutoFocusEditable } from "../../lib/mobileFocus";
import {
  createChannel,
  createTask,
  convertMessageToTask,
  listAgents,
  listChannels,
  listMembers,
  listMessages,
  listTasks,
  listThread,
  openWorkspaceStream,
  postMessage,
  updateTask,
} from "../../api/collab";
import type { TaskPatch } from "../../api/collab";
import type { AgentProfile, Channel, ChannelMessage, ChannelTask, TaskOwnerType, WorkspaceMember } from "../../types/collab";
import { ChannelView } from "./ChannelView";

/**
 * Chat view: channel sidebar (column 2) + the selected channel's
 * live conversation (column 3). Human messages only — the @mention → agent
 * bridge is Phase 3. History loads over REST; new channels/messages arrive over
 * the workspace WebSocket.
 */
export function ChannelsView({ accountId, workspaceName }: { accountId: string; workspaceName: string }) {
  const { user, client } = useAuth();
  const { t } = useI18n();
  const [channels, setChannels] = useState<Channel[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChannelMessage[]>([]);
  const [tasks, setTasks] = useState<ChannelTask[]>([]);
  // Loaded thread replies keyed by root message id (only expanded threads).
  const [threads, setThreads] = useState<Record<string, ChannelMessage[]>>({});
  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  const [agents, setAgents] = useState<AgentProfile[]>([]);
  const [memberNames, setMemberNames] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newTopic, setNewTopic] = useState("");
  const [newPrivate, setNewPrivate] = useState(false);

  const selectedRef = useRef<string | null>(null);
  selectedRef.current = selectedId;

  const selected = channels.find((c) => c.id === selectedId) ?? null;

  const loadChannels = useCallback(async () => {
    if (!accountId || !client) return;
    try {
      const [{ channels: list }, { members: mem }, { agents }] = await Promise.all([
        listChannels(client, accountId),
        listMembers(client, accountId),
        listAgents(client, accountId),
      ]);
      setChannels(list);
      setMembers(mem);
      setAgents(agents);
      const names: Record<string, string> = {};
      for (const m of mem) names[m.userId] = m.username ? `@${m.username}` : m.name || m.email || m.userId;
      // Agent authors (including the virtual cloud agent) display by name.
      for (const a of agents) names[a.id] = a.displayName;
      setMemberNames(names);
      setError(null);
      // Auto-open the first channel on desktop (both panes visible). On mobile
      // the panes stack, so land on the list and let the user pick — otherwise
      // "back" wouldn't stick (the next poll would re-select).
      if (!isMobileViewport()) setSelectedId((cur) => cur ?? list.find((c) => !c.archivedAt)?.id ?? null);
    } catch {
      setError(t("collab.channels.error"));
    }
  }, [accountId, client, t]);

  useEffect(() => {
    setSelectedId(null);
    void loadChannels();
  }, [loadChannels]);

  useEffect(() => {
    setThreads({});
    setSendError(null);
    if (!accountId || !client || !selectedId) {
      setMessages([]);
      setTasks([]);
      return;
    }
    let cancelled = false;
    void Promise.all([listMessages(client, accountId, selectedId), listTasks(client, accountId, selectedId)])
      .then(([messageResult, taskResult]) => {
        if (cancelled) return;
        setMessages(messageResult.messages);
        setTasks(taskResult.tasks);
      })
      .catch(() => {
        if (cancelled) return;
        setMessages([]);
        setTasks([]);
      });
    return () => {
      cancelled = true;
    };
  }, [accountId, client, selectedId]);

  useEffect(() => {
    if (!accountId || !client) return;
    const ws = openWorkspaceStream(client, accountId, (event) => {
      if (event.type === "channel.created") {
        setChannels((prev) => (prev.some((c) => c.id === event.channel.id) ? prev : [...prev, event.channel]));
      } else if (event.type === "channel.updated") {
        setChannels((prev) => prev.map((c) => (c.id === event.channel.id ? event.channel : c)));
      } else if (event.type === "channel.message.created") {
        if (event.channelId !== selectedRef.current) return;
        const msg = event.message;
        if (msg.threadId) {
          // A thread reply — append only into an already-expanded thread (the
          // root's replyCount chip updates via message.updated).
          const rootId = msg.threadId;
          setThreads((prev) => {
            const thread = prev[rootId];
            if (!thread || thread.some((m) => m.id === msg.id)) return prev;
            return { ...prev, [rootId]: [...thread, msg] };
          });
          return;
        }
        setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
      } else if (event.type === "channel.message.updated") {
        if (event.channelId !== selectedRef.current) return;
        const msg = event.message;
        setMessages((prev) => prev.map((m) => (m.id === msg.id ? msg : m)));
        if (msg.threadId) {
          setThreads((prev) => {
            const thread = prev[msg.threadId!];
            if (!thread) return prev;
            return { ...prev, [msg.threadId!]: thread.map((m) => (m.id === msg.id ? msg : m)) };
          });
        }
      } else if (event.type === "task.created") {
        if (event.channelId !== selectedRef.current) return;
        setTasks((prev) => (prev.some((task) => task.id === event.task.id) ? prev : [...prev, event.task]));
      } else if (event.type === "task.updated") {
        if (event.channelId !== selectedRef.current) return;
        setTasks((prev) => prev.map((task) => (task.id === event.task.id ? event.task : task)));
      }
    }, {
      onReconnect: () => {
        void loadChannels();
        const channelId = selectedRef.current;
        if (!channelId) return;
        void Promise.all([listMessages(client, accountId, channelId), listTasks(client, accountId, channelId)])
          .then(([messageResult, taskResult]) => {
            if (selectedRef.current !== channelId) return;
            setMessages(messageResult.messages);
            setTasks(taskResult.tasks);
          })
          .catch(() => undefined);
      },
    });
    return () => ws.close();
  }, [accountId, client, loadChannels]);

  const submitNewChannel = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const name = newName.trim();
      if (!name || !client) return;
      try {
        const { channel } = await createChannel(client, accountId, {
          name,
          topic: newTopic.trim() || undefined,
          visibility: newPrivate ? "private" : "account",
        });
        setChannels((prev) => (prev.some((c) => c.id === channel.id) ? prev : [...prev, channel]));
        setSelectedId(channel.id);
        setNewName("");
        setNewTopic("");
        setNewPrivate(false);
        setCreating(false);
      } catch {
        setError(t("collab.channels.error"));
      }
    },
    [accountId, client, newName, newTopic, newPrivate, t]
  );

  const send = useCallback(
    async (body: string) => {
      if (!selectedId || !client) return;
      setSendError(null);
      try {
        const { message, task } = await postMessage(client, accountId, selectedId, body);
        setMessages((prev) => (prev.some((m) => m.id === message.id) ? prev : [...prev, message]));
        if (task) setTasks((prev) => (prev.some((item) => item.id === task.id) ? prev : [...prev, task]));
      } catch (error) {
        setSendError(t("collab.channels.sendError"));
        throw error;
      }
    },
    [accountId, client, selectedId, t]
  );

  /** Expand a message's thread: fetch its replies once. */
  const openThread = useCallback(
    async (rootId: string) => {
      if (!selectedId || !client) return;
      try {
        const { messages: replies } = await listThread(client, accountId, selectedId, rootId);
        setThreads((prev) => ({ ...prev, [rootId]: replies }));
      } catch {
        /* leave collapsed */
      }
    },
    [accountId, client, selectedId]
  );

  const closeThread = useCallback((rootId: string) => {
    setThreads((prev) => {
      const next = { ...prev };
      delete next[rootId];
      return next;
    });
  }, []);

  const sendReply = useCallback(
    async (rootId: string, body: string) => {
      if (!selectedId || !client) return;
      setSendError(null);
      try {
        const { message, task } = await postMessage(client, accountId, selectedId, body, rootId);
        setThreads((prev) => {
          const thread = prev[rootId] ?? [];
          if (thread.some((m) => m.id === message.id)) return prev;
          return { ...prev, [rootId]: [...thread, message] };
        });
        // Optimistic replyCount bump on the root (the WS update confirms it).
        setMessages((prev) => prev.map((m) => (m.id === rootId ? { ...m, replyCount: m.replyCount + 1 } : m)));
        if (task) setTasks((prev) => (prev.some((item) => item.id === task.id) ? prev : [...prev, task]));
      } catch (error) {
        setSendError(t("collab.channels.sendError"));
        throw error;
      }
    },
    [accountId, client, selectedId, t]
  );

  const addTask = useCallback(
    async (input: { title: string; description?: string; ownerType?: TaskOwnerType | null; ownerId?: string | null }) => {
      if (!selectedId || !client) return;
      const { task } = await createTask(client, accountId, selectedId, input);
      setTasks((prev) => (prev.some((item) => item.id === task.id) ? prev : [...prev, task]));
    },
    [accountId, client, selectedId]
  );

  const patchTask = useCallback(
    async (taskId: string, patch: TaskPatch) => {
      if (!selectedId || !client) return;
      const { task } = await updateTask(client, accountId, selectedId, taskId, patch);
      setTasks((prev) => prev.map((item) => (item.id === task.id ? task : item)));
    },
    [accountId, client, selectedId]
  );

  const convertToTask = useCallback(
    async (messageId: string) => {
      if (!selectedId || !client) return;
      const { task } = await convertMessageToTask(client, accountId, selectedId, messageId);
      setTasks((prev) => (prev.some((item) => item.id === task.id) ? prev : [...prev, task]));
    },
    [accountId, client, selectedId]
  );

  return (
    <div className={`ws-view${selected ? " detail" : ""}`}>
      <aside className="ws-side">
        <div className="ws-side-head"><span className="ws-side-headtitle">{workspaceName}</span></div>
        <div className="ws-side-scroll">
          <div className="ws-side-section">
            <div className="ws-side-section-head">
              <span className="ws-side-label">{t("collab.channels")}</span>
              <button
                type="button"
                className="ws-icon-btn"
                onClick={() => setCreating((v) => !v)}
                title={t("collab.channels.new")}
                aria-label={t("collab.channels.new")}
              >
                {creating ? "×" : "+"}
              </button>
            </div>
            {creating ? (
              <form className="ws-channel-new" onSubmit={submitNewChannel}>
                <input
                  className="ws-input"
                  placeholder={t("collab.channels.namePlaceholder")}
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  autoFocus={shouldAutoFocusEditable()}
                />
                <input
                  className="ws-input"
                  placeholder={t("collab.channels.topicPlaceholder")}
                  value={newTopic}
                  onChange={(e) => setNewTopic(e.target.value)}
                />
                <label className="ws-check">
                  <input type="checkbox" checked={newPrivate} onChange={(e) => setNewPrivate(e.target.checked)} />
                  {t("collab.channels.private")}
                </label>
                <button type="submit" className="ws-btn small" disabled={!newName.trim()}>
                  {t("collab.channels.create")}
                </button>
              </form>
            ) : null}
            {error ? <p className="ws-warn">{error}</p> : null}
            {channels.length === 0 ? (
              <p className="ws-side-empty">{t("collab.channels.empty")}</p>
            ) : (
              <ul className="ws-channel-list">
                {channels.map((c) => (
                  <li key={c.id}>
                    <button
                      type="button"
                      className={`ws-channel-item${c.id === selectedId ? " active" : ""}`}
                      onClick={() => setSelectedId(c.id)}
                    >
                      <span className="ws-channel-hash">{c.visibility === "private" ? "🔒" : "#"}</span>
                      <span className="ws-channel-name">{c.name}</span>
                      {c.archivedAt ? <span className="ws-channel-badge">{t("collab.channels.archived")}</span> : null}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </aside>

      <section className="ws-content">
        {selected ? (
          <>
            <button type="button" className="ws-mback" onClick={() => setSelectedId(null)}>‹ {t("collab.channels")}</button>
            <ChannelView
              channel={selected}
              messages={messages}
              tasks={tasks}
              threads={threads}
              memberNames={memberNames}
              members={members}
              agents={agents}
              memberCount={members.length}
              currentUserId={user?.id ?? ""}
              sendError={sendError}
              onSend={send}
              onSendReply={sendReply}
              onOpenThread={openThread}
              onCloseThread={closeThread}
              onCreateTask={addTask}
              onUpdateTask={patchTask}
              onConvertToTask={convertToTask}
            />
          </>
        ) : (
          <div className="ws-content-empty">{t("collab.channels.selectPrompt")}</div>
        )}
      </section>
    </div>
  );
}
